import React, { useState, useEffect } from 'react';
import axios from './axiosInstance';           // adjust import if needed
import './styles/DragAndDropUploader.css';
import ProtectedComponent from './ProtectedComponent';

const DragAndDropUploader = () => {
  const [uploaded, setUploaded]  = useState([]);      // [{name,size,uploadedBy,uploadedAt}]
  const [newFiles, setNewFiles]  = useState([]);      // [File]
  const [selected, setSelected]  = useState(null);    // {name,size}
  const [expires,  setExpires]   = useState(14);
  const [unit,     setUnit]      = useState('days');
  const [oneTime,  setOneTime]   = useState(false);
  const [password, setPassword]  = useState('');
  const [links,    setLinks]     = useState([]);
  const [msg,      setMsg]       = useState('');
  const [tempLink, setTempLink]  = useState(null);
  const [tempLinkHasPass, setTempLinkHasPass] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeLinksCount, setActiveLinksCount] = useState({});
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [showDownloads, setShowDownloads] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'uploadedAt', direction: 'desc' });
  const [showUploadLinkPanel, setShowUploadLinkPanel] = useState(false);
  const [ulExpires, setUlExpires] = useState(24);
  const [ulUnit, setUlUnit] = useState('hours');
  const [ulPassword, setUlPassword] = useState('');
  const [generatedUploadLink, setGeneratedUploadLink] = useState(null);
  const [uploadLinks, setUploadLinks] = useState([]);
  const [ulCopied, setUlCopied] = useState(false);

  /* fetch list and active link counts once */
  useEffect(() => {
    axios.get('/files').then(({data})=>setUploaded(data)).catch(console.error);
    axios.get('/files/active-links-count').then(({data})=>setActiveLinksCount(data)).catch(console.error);
    axios.get('/files/upload-links').then(({data})=>setUploadLinks(data)).catch(console.error);
  }, []);

  /* load links when file selected */
  useEffect(() => {
    if (!selected) { setLinks([]); setDownloadHistory([]); return; }
    axios.get(`/files/temp-links/${encodeURIComponent(selected.name)}`)
         .then(({data})=>setLinks(data)).catch(console.error);
    // Also fetch download history for this file
    axios.get(`/files/all-downloads/${encodeURIComponent(selected.name)}`)
         .then(({data})=>setDownloadHistory(data)).catch(console.error);
  }, [selected]);

  /* refresh active links count */
  const refreshActiveLinksCount = () => {
    axios.get('/files/active-links-count').then(({data})=>setActiveLinksCount(data)).catch(console.error);
  };

  /* filter and sort files */
  const filteredFiles = uploaded
    .filter(f => f.name.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal = a[key];
      let bVal = b[key];

      // Handle active links sorting
      if (key === 'activeLinks') {
        aVal = activeLinksCount[a.name] || 0;
        bVal = activeLinksCount[b.name] || 0;
      }

      // Handle date comparison
      if (key === 'uploadedAt') {
        aVal = new Date(aVal || 0).getTime();
        bVal = new Date(bVal || 0).getTime();
      }

      // Handle string comparison (case-insensitive)
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });

  /* handle column sort */
  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  /* sort indicator */
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕';
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  /* format date */
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /* helper */
  const seconds = unit === 'hours' ? expires*3600 :
                  unit === 'days'  ? expires*86400 : expires*60;
  const ulSeconds = ulUnit === 'hours' ? ulExpires*3600 :
                    ulUnit === 'days'  ? ulExpires*86400 : ulExpires*60;

  /* generate upload link */
  const genUploadLink = () => {
    axios.post('/files/generate-upload-link', {
      expiresIn: ulSeconds,
      password: ulPassword || undefined,
    })
    .then(({data}) => {
      setGeneratedUploadLink(data.uploadUrl);
      setUlPassword('');
      setUlCopied(false);
      axios.get('/files/upload-links').then(({data})=>setUploadLinks(data)).catch(console.error);
    })
    .catch(console.error);
  };

  const revokeUploadLink = (token) => {
    axios.delete(`/files/upload-link/${token}`)
      .then(() => setUploadLinks(uploadLinks.filter(l => l._id !== token)))
      .catch(console.error);
  };

  /* upload */
  const doUpload = () => {
    if (!newFiles.length) return setMsg('No file selected');
    const fd = new FormData();
    newFiles.forEach(f => fd.append('files', f));

    setUploading(true);
    setUploadProgress(0);
    setMsg('');

    axios.post('/files/upload', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (progressEvent) => {
        const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percent);
      }
    })
    .then(() => {
      setMsg('Uploaded');
      // Refresh the file list to get proper metadata from server
      axios.get('/files').then(({data}) => setUploaded(data)).catch(console.error);
      setNewFiles([]);
    })
    .catch(e => {
      setMsg('Upload failed');
      console.error(e);
    })
    .finally(() => {
      setUploading(false);
      setUploadProgress(0);
    });
  };

  /* generate link */
  const genLink = () => {
    if (!selected) return;
    const hasPass = !!password;
    axios.post('/files/generate-temp-link',{
      fileName:selected.name,
      expiresIn: oneTime ? undefined : seconds,
      oneTime,
      password: password || undefined,
    })
    .then(({data})=>{
      setTempLink(data.downloadUrl);
      setTempLinkHasPass(hasPass);
      // Refresh links for this file
      axios.get(`/files/temp-links/${encodeURIComponent(selected.name)}`)
           .then(({data})=>setLinks(data)).catch(console.error);
      setPassword('');
      refreshActiveLinksCount();
    })
    .catch(console.error);
  };

  /* drag & drop handlers */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = Array.from(e.dataTransfer.files);
    setNewFiles([...newFiles, ...files]);
  };

  return (
    <ProtectedComponent functionCode="OP05" functionName="Drag & Drop Uploader">
      <div className="container">
      <h2>Temporary Link Creator</h2>

      {/* upload / list */}
      <div className="top-boxes">
        <div className="upload-box">
          <div
            className="drop-zone"
            onDragOver={handleDragOver}
            onDrop={handleDrop}
          >
            {newFiles.length === 0 ? (
              <p>Drag & drop files here</p>
            ) : (
              <div className="selected-files">
                {newFiles.map(f=>(
                  <div key={f.name} className="selected-file-item">
                    <span>{f.name}</span>
                    <button
                      className="remove-file-btn"
                      onClick={() => setNewFiles(newFiles.filter(file => file.name !== f.name))}
                    >×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="button-row">
            <label htmlFor="fi" className="choose-btn">Choose File</label>
            <input id="fi" type="file" multiple onChange={e=>setNewFiles([...newFiles,...e.target.files])} disabled={uploading}/>
            {newFiles.length > 0 && (
              <button className="upload-action" onClick={doUpload} disabled={uploading}>
                {uploading ? `Uploading... ${uploadProgress}%` : 'Upload'}
              </button>
            )}
          </div>
          {uploading && (
            <div className="upload-progress-container">
              <div className="upload-progress-bar" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          )}
          {msg && <p className="upload-msg">{msg}</p>}
        </div>

        <div className="existing-files-box">
          <h3>Uploaded Files</h3>
          <div className="search-box">
            <input
              type="text"
              placeholder="Search files..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="file-table-wrapper">
            <table className="file-table">
              <thead>
                <tr>
                  <th className="sortable-header" onClick={() => handleSort('name')}>
                    File Name <span className="sort-icon">{getSortIcon('name')}</span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('activeLinks')}>
                    Active Links <span className="sort-icon">{getSortIcon('activeLinks')}</span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('uploadedBy')}>
                    Uploaded By <span className="sort-icon">{getSortIcon('uploadedBy')}</span>
                  </th>
                  <th className="sortable-header" onClick={() => handleSort('uploadedAt')}>
                    Date Uploaded <span className="sort-icon">{getSortIcon('uploadedAt')}</span>
                  </th>
                  <th className="action-header"></th>
                </tr>
              </thead>
              <tbody>
                {filteredFiles.map(f => (
                  <tr
                    key={f.name}
                    className={selected?.name === f.name ? 'selected-row' : ''}
                    onClick={() => { setSelected(f); setTempLink(null); setShowDownloads(false); }}
                  >
                    <td className="file-name-cell">
                      <span className="file-name-text">{f.name}</span>
                    </td>
                    <td className="active-links-cell">
                      {activeLinksCount[f.name] > 0 ? (
                        <span className="active-badge">{activeLinksCount[f.name]}</span>
                      ) : (
                        <span className="no-links">0</span>
                      )}
                    </td>
                    <td className="uploader-cell">{f.uploadedBy || 'Unknown'}</td>
                    <td className="date-cell">{formatDate(f.uploadedAt)}</td>
                    <td className="action-cell">
                      <button
                        className="delete-btn"
                        onClick={e => {
                          e.stopPropagation();
                          if (window.confirm(`Delete "${f.name}"?`)) {
                            axios.delete(`/files/${encodeURIComponent(f.name)}`)
                              .then(() => {
                                setUploaded(uploaded.filter(x => x.name !== f.name));
                                refreshActiveLinksCount();
                              })
                              .catch(console.error);
                          }
                        }}
                      >×</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filteredFiles.length === 0 && (
              <div className="no-files-message">No files found</div>
            )}
          </div>
        </div>
      </div>

      {/* upload link creator */}
      <div className="upload-link-section">
        <button className="upload-link-toggle" onClick={() => setShowUploadLinkPanel(!showUploadLinkPanel)}>
          {showUploadLinkPanel ? 'Hide Upload Links' : 'Create One-Time Upload Link'}
        </button>
        {showUploadLinkPanel && (
          <div className="upload-link-panel">
            <div className="upload-link-controls">
              <span>
                Expires in:
                <input type="number" value={ulExpires} min="1" onChange={e => setUlExpires(e.target.value)} />
                <select value={ulUnit} onChange={e => setUlUnit(e.target.value)}>
                  <option value="minutes">min</option>
                  <option value="hours">hr</option>
                  <option value="days">day</option>
                </select>
              </span>
              <label>
                Password:
                <input type="password" value={ulPassword} placeholder="optional" onChange={e => setUlPassword(e.target.value)} />
              </label>
              <button onClick={genUploadLink}>Generate Upload Link</button>
            </div>
            {generatedUploadLink && (
              <div className="generated-upload-link">
                <input type="text" readOnly value={generatedUploadLink} onClick={e => e.target.select()} />
                <button onClick={() => { navigator.clipboard.writeText(generatedUploadLink); setUlCopied(true); }}>
                  {ulCopied ? 'Copied!' : 'Copy'}
                </button>
              </div>
            )}
            {uploadLinks.length > 0 && (
              <div className="upload-links-list-section">
                <h5>Active Upload Links ({uploadLinks.filter(l => !l.used).length})</h5>
                <ul className="upload-links-list">
                  {uploadLinks.map(l => (
                    <li key={l._id} className={l.used ? 'used-link' : ''}>
                      <div className="ul-link-info">
                        <span className="ul-token">{l._id.slice(0, 12)}...</span>
                        {l.password && <span className="ul-badge ul-pw">PW</span>}
                        {l.used
                          ? <span className="ul-badge ul-used">Used: {l.uploadedFileName}</span>
                          : <span className="ul-badge ul-active">Active</span>
                        }
                        <span className="ul-expires">Exp: {new Date(l.expiresAt).toLocaleString()}</span>
                      </div>
                      {!l.used && (
                        <button onClick={() => revokeUploadLink(l._id)}>Revoke</button>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      {/* link creator - compact horizontal layout */}
      {selected && (
        <div className="selected-file-box">
          <h4>{selected.name}</h4>

          <div className="link-controls">
            <label>
              <input type="checkbox" checked={oneTime} onChange={e=>setOneTime(e.target.checked)}/> One-time
            </label>
            <span>
              <input type="number" value={expires} disabled={oneTime} onChange={e=>setExpires(e.target.value)}/>
              <select value={unit} disabled={oneTime} onChange={e=>setUnit(e.target.value)}>
                <option value="minutes">min</option>
                <option value="hours">hr</option>
                <option value="days">day</option>
              </select>
            </span>
            <label>
              Password:
              <input type="password" value={password} placeholder="optional" onChange={e=>setPassword(e.target.value)}/>
            </label>
            <button onClick={genLink}>Generate Link</button>
          </div>

          {tempLink && (
            <div className="generated-link">
              <a href={tempLink} target="_blank" rel="noreferrer"
                 onClick={e=>{
                   if(tempLinkHasPass){
                     e.preventDefault();
                     const pw = prompt('Enter password:');
                     if(pw) window.open(`${tempLink}?password=${encodeURIComponent(pw)}`,'_blank');
                   }
                 }}>{tempLinkHasPass?'🔒 ':''}{tempLink}</a>
            </div>
          )}

          <div className="links-section">
            <div className="links-header">
              <h5>Active Links ({links.length})</h5>
              <button className="toggle-downloads-btn" onClick={() => setShowDownloads(!showDownloads)}>
                {showDownloads ? 'Hide Downloads' : 'Show Downloads'}
              </button>
            </div>
            {links.length ? (
              <ul className="links-list">
                {links.map(l=>(
                  <li key={l._id}>
                    <div className="link-info">
                      <a href={`/api/files/temp/${l._id}`} target="_blank" rel="noreferrer"
                         onClick={e=>{
                           if(l.password){
                             e.preventDefault();
                             const pw = prompt('Enter password:');
                             if(pw) window.open(`/api/files/temp/${l._id}?password=${encodeURIComponent(pw)}`,'_blank');
                           }
                         }}>
                        {l.password?'🔒 ':''}{l.oneTime?'[1×] ':''}{new Date(l.expiresAt).toLocaleString()}
                      </a>
                      <span className="download-count">({l.downloads?.length || 0} dl)</span>
                    </div>
                    <button onClick={()=>{
                        axios.delete(`/files/temp-link/${l._id}`)
                             .then(()=>{
                               setLinks(links.filter(x=>x._id!==l._id));
                               refreshActiveLinksCount();
                             })
                             .catch(console.error);
                      }}>Expire</button>
                  </li>
                ))}
              </ul>
            ):<p style={{color: '#8fa3bf', fontSize: '0.75rem', margin: '0.25rem 0'}}>No active links</p>}

            {showDownloads && downloadHistory.length > 0 && (
              <div className="download-history">
                <h5>Download History</h5>
                <div className="downloads-table-wrapper">
                  <table className="downloads-table">
                    <thead>
                      <tr>
                        <th>Time</th>
                        <th>IP</th>
                        <th>User Agent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {downloadHistory.map((d, i) => (
                        <tr key={i}>
                          <td>{new Date(d.timestamp).toLocaleString()}</td>
                          <td>{d.ip || '?'}</td>
                          <td className="user-agent-cell" title={d.userAgent}>
                            {d.userAgent?.substring(0, 40) || '?'}{d.userAgent?.length > 40 ? '...' : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
    </ProtectedComponent>
  );
};

export default DragAndDropUploader;
